/**
 * part-align.js — window.PartAlign / module.exports
 *
 * РЕШЕНИЕ, что сделать с партией, чтобы она легла на опорную запись: сетка
 * долей партии сопоставляется с сеткой опоры, и из этого выходит либо кривая
 * натяжения (warp-map.js), либо постоянный коэффициент, либо просто сдвиг,
 * либо честный отказ.
 *
 * ── Лестница, и почему она видимая ────────────────────────────────────────
 * Сетка долей строится не всегда: у легатной скрипки без удара онсетов почти
 * нет, и DP-трекер честно отвечает низким согласием. Молча натянуть такую
 * партию значит испортить её ради цифры. Поэтому здесь не «выровняли», а
 * решение с ПРИЧИНОЙ, которую тул показывает человеку:
 *
 *     warp    доля в долю        — сетки обеих партий надёжны
 *     rate    один коэффициент   — темпы известны, а доли не сопоставились
 *     offset  только сдвиг       — темп тот же, просто начали в разное время
 *     manual  ничего             — оснований трогать звук нет, двигайте сами
 *
 * ── Метрический уровень ───────────────────────────────────────────────────
 * ⚠️ Трекер может дать половинный или удвоенный темп — это его известное
 * свойство (см. шапку beat-grid.js: «сетка на настоящих ударах, но не там, где
 * их считает человек»). Поэтому доли сопоставляются не одна к одной, а через
 * ОТНОШЕНИЕ темпов, округлённое к ближайшему музыкальному: 1/2, 2/3, 1, 3/2, 2.
 * Без этого партия на 90 при опоре 120 не сопоставится вовсе.
 *
 * Чистая математика: ни DOM, ни Web Audio. Проверяется в Node на синтетике с
 * известной истиной (tests/js/part-align.test.mjs).
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.PartAlign = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* Музыкально осмысленные отношения темпов. Половина и двойка — уровни
       трекера; две трети и полтора — переход между двудольным и трёхдольным
       счётом, который живые музыканты делают постоянно. */
    var RATIOS = [0.5, 2 / 3, 1, 1.5, 2];
    /* Насколько доля может не совпасть, чтобы её ещё считать той же. 70 мс —
       чуть выше порога «слышно, что вразнобой» (50 мс): ниже него мы уже
       считаем попаданием, выше — это другая доля. */
    var MATCH_S = 0.07;
    /* Сколько долей обязано сопоставиться, чтобы кривой можно было верить. */
    var MIN_PAIRS = 4;
    /* Минимальная доля сопоставленных долей партии. Ниже — сетки говорят о
       разном, и кривая по ним будет фантазией. */
    var MIN_SHARE = 0.55;
    /* Согласие сетки, ниже которого она не основание (beat-grid.alignment). */
    var MIN_ALIGNMENT = 0.6;
    /* Насколько доли могут разойтись ПОСЛЕ подгонки прямой, чтобы кривая была
       уже не нужна. 8 мс — заметно ниже слышимых 50 и ниже собственной ошибки
       трекера, то есть кривая тут ловила бы шум, а не музыку. */
    var LINEAR_OK_MS = 8;

    function isNum(v) { return typeof v === 'number' && isFinite(v); }
    function median(a) {
        if (!a.length) return 0;
        var s = a.slice().sort(function (x, y) { return x - y; });
        return s[s.length >> 1];
    }

    /**
     * Ближайшее музыкальное отношение темпов опоры к темпу партии.
     * Возвращает и само отношение, и насколько пришлось округлять: сильное
     * округление — признак, что темпы просто разные, а не уровень перепутан.
     */
    function ratioOf(bpmPart, bpmAnchor) {
        if (!isNum(bpmPart) || !isNum(bpmAnchor) || bpmPart <= 0 || bpmAnchor <= 0) {
            return { ratio: 1, off: Infinity };
        }
        var want = bpmAnchor / bpmPart;
        var best = 1, bestOff = Infinity;
        for (var i = 0; i < RATIOS.length; i++) {
            var off = want > RATIOS[i] ? want / RATIOS[i] : RATIOS[i] / want;
            if (off < bestOff) { bestOff = off; best = RATIOS[i]; }
        }
        return { ratio: best, off: bestOff };
    }

    /**
     * Сдвиг, при котором доли совпадают ЧАЩЕ ВСЕГО, для заданного наклона.
     *
     * ⚠️⚠️ Без этого шага весь разбор ложен, и ложен ТИХО. Доли периодичны:
     * при любом сдвиге каждая доля партии оказывается не дальше полудоли от
     * какой-нибудь доли опоры, поэтому «ближайшая» находится ВСЕГДА — просто
     * не та. Подгонка прямой по таким парам сходится к тождеству, и тул
     * честно докладывает «сдвинута на 0 мс, звук не тронут», не сделав ничего.
     *
     * Поймано стендом на точной истине (одна и та же запись, вторая копия
     * замедлена на 4 % и сдвинута на 0.35 с): копии складывались с
     * коэффициентом 1.43 вместо 2.0 — то есть не складывались вовсе, — а
     * интерфейс сообщал об успехе. Ни одна проверка «не упало ли» этого бы не
     * увидела.
     *
     * Считается голосованием: каждая пара (доля партии, доля опоры) подаёт
     * голос за свой сдвиг, голоса сваливаются в корзины по полдопуска, и
     * побеждает самая полная. Это то же, что пик кросс-корреляции, только по
     * долям, а их десятки — значит дёшево.
     */
    function voteOffset(partBeats, anchorBeats, scale) {
        var BIN = MATCH_S / 2;
        var votes = Object.create(null), bestKey = null, bestN = 0;
        for (var i = 0; i < partBeats.length; i++) {
            var at = scale * partBeats[i];
            for (var j = 0; j < anchorBeats.length; j++) {
                var d = anchorBeats[j] - at;
                var key = Math.round(d / BIN);
                /* Соседние корзины тоже считаются своими: истинный сдвиг может
                   лечь на границу, и без этого он делится пополам между двумя. */
                for (var k = -1; k <= 1; k++) {
                    var kk = key + k;
                    votes[kk] = (votes[kk] || 0) + 1;
                    if (votes[kk] > bestN) { bestN = votes[kk]; bestKey = kk; }
                }
            }
        }
        if (bestKey === null) return { offsetSec: 0, votes: 0 };
        /* Уточняем центр победившей корзины средним по её голосам. */
        var sum = 0, n = 0;
        for (var a = 0; a < partBeats.length; a++) {
            var at2 = scale * partBeats[a];
            for (var b = 0; b < anchorBeats.length; b++) {
                var dd = anchorBeats[b] - at2;
                if (Math.abs(dd - bestKey * BIN) <= BIN) { sum += dd; n++; }
            }
        }
        return { offsetSec: n ? sum / n : bestKey * BIN, votes: bestN };
    }

    /**
     * Сопоставление долей: каждая доля партии ищет БЛИЖАЙШУЮ долю опоры —
     * но не к себе, а к тому месту, где её ждут по текущей прямой
     * «время опоры = c + scale · время партии».
     *
     * ⚠️ Почему прямая пересчитывается, а не берётся сдвиг раз и навсегда.
     * Сопоставление с одним фиксированным сдвигом ловит только начало партии:
     * при живом уводе темпа доли расходятся, и к середине расхождение уже
     * больше допуска. Замер на синтетике (32 доли, увод 120 → 126, сдвиг
     * известен точно): фиксированный сдвиг сопоставил 14 долей из 32 и решение
     * скатилось на постоянный коэффициент — то есть ровно в тот случай, ради
     * которого кривая и заводилась, она НЕ включалась. Поэтому прямая
     * подгоняется по уже сопоставленным долям и сопоставление повторяется;
     * остаточную неравномерность дальше уже забирает кривая.
     *
     * Отношение темпов сюда приходит НАЧАЛЬНЫМ наклоном, а не индексом: при
     * половинном или удвоенном уровне трекера доля партии просто находит
     * каждую вторую долю опоры, и ничего перебирать не надо.
     */
    function pairsFrom(partBeats, anchorBeats, opts) {
        opts = opts || {};
        var scale = isNum(opts.scale) && opts.scale > 0 ? opts.scale : 1;
        var c = isNum(opts.offsetSec) ? opts.offsetSec : 0;
        if (!partBeats || !anchorBeats || partBeats.length < 2 || anchorBeats.length < 2) {
            return { pairs: [], matched: 0, residualMs: 0, scale: scale, offsetSec: c };
        }

        var pairs = [], errs = [];
        for (var pass = 0; pass < 4; pass++) {
            pairs = []; errs = [];
            var lastOut = -Infinity, lastIn = -Infinity;
            for (var k = 0; k < partBeats.length; k++) {
                var inT = partBeats[k];
                var want = c + scale * inT;
                /* Ближайшая доля опоры к ожидаемому месту. */
                var bestJ = -1, bestD = Infinity;
                for (var j = 0; j < anchorBeats.length; j++) {
                    var d = Math.abs(anchorBeats[j] - want);
                    if (d < bestD) { bestD = d; bestJ = j; }
                }
                if (bestJ < 0 || bestD > MATCH_S) continue;
                var outT = anchorBeats[bestJ];
                if (inT <= lastIn || outT <= lastOut) continue;   // строго вперёд
                pairs.push([inT, outT]);
                errs.push(bestD * 1000);
                lastIn = inT; lastOut = outT;
            }
            if (pairs.length < 2) break;
            /* Подгонка прямой по сопоставленным долям (метод наименьших
               квадратов). Следующий проход ищет уже вокруг неё. */
            var n = pairs.length, sx = 0, sy = 0, sxx = 0, sxy = 0;
            for (var i = 0; i < n; i++) { sx += pairs[i][0]; sy += pairs[i][1]; sxx += pairs[i][0] * pairs[i][0]; sxy += pairs[i][0] * pairs[i][1]; }
            var den = n * sxx - sx * sx;
            if (den <= 0) break;
            var ns = (n * sxy - sx * sy) / den;
            var nc = (sy - ns * sx) / n;
            if (!isNum(ns) || !isNum(nc) || ns <= 0) break;
            if (Math.abs(ns - scale) < 1e-6 && Math.abs(nc - c) < 1e-6) break;   // сошлось
            scale = ns; c = nc;
        }

        return {
            pairs: pairs,
            matched: pairs.length,
            residualMs: median(errs),
            scale: scale,
            offsetSec: c
        };
    }

    /**
     * Что делать с партией. Вход — то, что уже посчитано над обоими буферами.
     *
     *   partBeats/anchorBeats   сетки долей (BeatGrid.track().beats)
     *   bpmPart/bpmAnchor       их темпы
     *   alignment               согласие сетки партии с её же ударами (0..1)
     *   anchorAlignment         то же для опоры
     *   offsetSec               грубое смещение (AudioAlign или рука человека)
     *   sampleRate/inFrames     для проверки кривой на выполнимость
     *
     * Возвращает { mode, reason, warp?, rate?, offsetSec, matched, correctedMs }.
     * `correctedMs` — насколько разъехались бы доли БЕЗ натяжения: это то, что
     * тул показывает человеку как «ради чего».
     */
    function decide(o) {
        o = o || {};
        var offset = isNum(o.offsetSec) ? o.offsetSec : 0;
        var out = { mode: 'manual', reason: 'no_grid', offsetSec: offset, matched: 0, correctedMs: 0 };

        var haveGrids = o.partBeats && o.anchorBeats
            && o.partBeats.length >= MIN_PAIRS && o.anchorBeats.length >= MIN_PAIRS;
        if (!haveGrids) return out;

        var weak = (isNum(o.alignment) && o.alignment < MIN_ALIGNMENT)
            || (isNum(o.anchorAlignment) && o.anchorAlignment < MIN_ALIGNMENT);

        /* ⚠️ Начальных гипотез несколько, и это не перестраховка. Я сперва
           брал наклоном отношение темпов — и партия на 60 против опоры 120 не
           сопоставилась вовсе. Причина в том, что «другой темп» и «другой
           МЕТРИЧЕСКИЙ УРОВЕНЬ» — разные вещи: половинный счёт не требует
           трогать звук, доли просто ложатся через одну, и растяжение вдвое
           было бы порчей. Отличить их заранее нельзя, зато можно попробовать
           обе и взять ту, при которой сошлось больше долей.

           Единица идёт первой и выигрывает ничьи: не трогать звук — лучший
           исход, а не запасной. */
        var starts = [1];
        if (isNum(o.bpmPart) && isNum(o.bpmAnchor) && o.bpmPart > 0 && o.bpmAnchor > 0) {
            var direct = o.bpmPart / o.bpmAnchor;
            if (Math.abs(direct - 1) > 0.002) starts.push(direct);
            for (var ri = 0; ri < RATIOS.length; ri++) {
                var cand = direct * RATIOS[ri];
                if (cand > 0.4 && cand < 2.5 && Math.abs(cand - 1) > 0.002) starts.push(cand);
            }
        }
        /* Для каждой гипотезы наклона пробуем ДВА начальных сдвига: тот, что
           дал грубый поиск по звуку (если он вообще уверен), и тот, за который
           проголосовали сами доли. Второй нужен всегда: первый часто равен
           нулю, а ноль — это не «сдвига нет», а «мы его не знаем». */
        var m = null;
        for (var si = 0; si < starts.length; si++) {
            var cands = [voteOffset(o.partBeats, o.anchorBeats, starts[si]).offsetSec];
            if (Math.abs(offset) > 1e-6) cands.push(offset);
            for (var ci = 0; ci < cands.length; ci++) {
                var t = pairsFrom(o.partBeats, o.anchorBeats, { scale: starts[si], offsetSec: cands[ci] });
                if (!m || t.matched > m.matched) m = t;
            }
        }
        var reach = Math.min(o.partBeats.length, o.anchorBeats.length);
        var share = reach > 0 ? m.matched / reach : 0;

        /* Насколько доли разъедутся, если просто сдвинуть партию: разность
           между тем, где доля партии окажется, и тем, где её ждут. Это и есть
           ответ на вопрос «зачем вообще тянуть». */
        if (m.matched >= 2) {
            /* Сравнение с ЛУЧШИМ, что может дать один коэффициент: прямая,
               подогнанная по тем же долям. Разность с ней и есть то, что
               забирает кривая, — честная мера «ради чего». */
            var drift = [];
            for (var i = 0; i < m.pairs.length; i++) {
                var lin = m.offsetSec + m.scale * m.pairs[i][0];
                drift.push(Math.abs(lin - m.pairs[i][1]) * 1000);
            }
            out.correctedMs = Math.round(Math.max.apply(null, drift));
        }
        out.matched = m.matched;

        if (weak) { out.reason = 'weak_grid'; }

        if (!weak && m.matched >= MIN_PAIRS && share >= MIN_SHARE) {
            /* ⚠️ Берём САМОЕ ЩАДЯЩЕЕ преобразование, которым цель достижима.
               Натяжение — не бесплатная операция: это переразложение сигнала, и
               делать его там, где хватает сдвига, значит платить качеством за
               ничто. Если после подгонки прямой доли уже сходятся, кривая не
               нужна; если при этом и наклон единичный — не нужно даже
               изменение скорости. Порог 8 мс — заметно ниже слышимых 50. */
            if (out.correctedMs <= LINEAR_OK_MS) {
                if (Math.abs(m.scale - 1) < 0.002) {
                    out.mode = 'offset';
                    out.reason = 'same_tempo';
                    out.offsetSec = m.offsetSec;
                    return out;
                }
                out.mode = 'rate';
                out.reason = 'steady_tempo';
                /* rate у движка — сколько ВХОДА на единицу ВЫХОДА. */
                out.rate = 1 / m.scale;
                out.offsetSec = m.offsetSec;
                return out;
            }
            /* Кривая строится по парам, СДВИНУТЫМ в шкалу опоры: время выхода
               уже учитывает смещение, поэтому отдельного сдвига не нужно. */
            out.mode = 'warp';
            out.reason = 'beats_matched';
            out.warp = m.pairs.map(function (p) { return [p[0], p[1]]; });
            out.offsetSec = 0;
            return out;
        }

        /* Доли не сопоставились, но темпы известны — можно хотя бы уравнять
           скорость. Тон при этом не уезжает (за это отвечает Signalsmith). */
        if (isNum(o.bpmPart) && isNum(o.bpmAnchor) && o.bpmPart > 0 && o.bpmAnchor > 0) {
            var rate = o.bpmPart / o.bpmAnchor;
            if (rate > 0.5 && rate < 2 && Math.abs(rate - 1) > 0.005) {
                out.mode = 'rate';
                out.reason = weak ? 'weak_grid' : 'beats_unmatched';
                out.rate = rate;
                out.offsetSec = offset;
                return out;
            }
            if (Math.abs(rate - 1) <= 0.005) {
                out.mode = 'offset';
                out.reason = 'same_tempo';
                out.offsetSec = offset;
                return out;
            }
        }

        return out;
    }

    return {
        RATIOS: RATIOS,
        MATCH_S: MATCH_S,
        MIN_PAIRS: MIN_PAIRS,
        MIN_SHARE: MIN_SHARE,
        MIN_ALIGNMENT: MIN_ALIGNMENT,
        LINEAR_OK_MS: LINEAR_OK_MS,
        ratioOf: ratioOf,
        voteOffset: voteOffset,
        pairsFrom: pairsFrom,
        decide: decide
    };
});
